/**
 * Utility functions for formatting course data
 */

/**
 * Format price with proper currency display
 */
export const formatPrice = (fee: number) => {
  if (fee === 0) return 'Miễn phí';
  
  // Handle both string and object returns for backward compatibility
  const formatted = new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0
  }).format(fee);
  
  return {
    current: formatted,
    original: null,
    isPromotion: false
  };
};

/**
 * Simple price formatter that returns just the formatted string
 */
export const formatPriceSimple = (fee: number): string => {
  if (fee === 0) return 'Miễn phí';
  
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0
  }).format(fee);
};

/**
 * Compact VND for promo UI: 3.000.000 → 3tr, 7.500.000 → 7,5tr
 */
export const formatPriceCompact = (fee: number): string => {
  if (fee === 0) return '0đ';
  if (fee >= 1_000_000) {
    const millions = fee / 1_000_000;
    const label =
      Number.isInteger(millions)
        ? String(millions)
        : millions.toFixed(1).replace('.', ',').replace(/,0$/, '');
    return `${label}tr`;
  }
  if (fee >= 1_000) {
    const thousands = fee / 1_000;
    const label =
      Number.isInteger(thousands)
        ? String(thousands)
        : thousands.toFixed(1).replace('.', ',').replace(/,0$/, '');
    return `${label}k`;
  }
  return `${fee}đ`;
};

/**
 * Format duration in human-readable format
 */
export const formatDuration = (hours: number): string => {
  if (hours < 1) {
    return `${Math.round(hours * 60)} phút`;
  }
  
  if (hours % 1 === 0) {
    return `${hours} giờ`;
  }
  
  return `${hours.toFixed(1)} giờ`;
};

/**
 * Truncate text to specified length with ellipsis
 */
export const truncateText = (text: string, maxLength: number = 120): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Get duration range category
 */
export const getDurationRange = (hours: number): 'short' | 'medium' | 'long' => {
  if (hours < 10) return 'short';
  if (hours <= 50) return 'medium';
  return 'long';
};

/**
 * Format student count
 */
export const formatStudentCount = (count: number): string => {
  if (count === 0) return 'Chưa có học viên';
  if (count === 1) return '1 học viên';
  return `${count.toLocaleString('vi-VN')} học viên`;
};

/**
 * Format rating display
 */
export const formatRating = (rating: number, reviewCount: number) => {
  if (reviewCount === 0) {
    return { display: 'Chưa có đánh giá', hasRating: false };
  }
  
  return {
    display: `${rating.toFixed(1)} (${reviewCount} đánh giá)`,
    hasRating: true,
    rating: rating.toFixed(1),
    reviewCount
  };
};